import { useState } from 'react';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';

import { ConfirmationDialogV2, ErpDataTableV2, ErpInputTableV2, BulkDeleteDialogV4 } from '@/components/custom/erp';
import { DonHangMuaTrongNuocSearchProvider, useDonHangMuaTrongNuocSearchContext } from './context';
import { getDataTableColumns, getInputTableColumns } from './cols-definition';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { initialSearchValues, SearchFormValues } from './schema';
import { DonMuaHang, DonMuaHangInput } from '@/types/schemas';
import { useSearchFieldState, useSearchTable } from './hooks';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

function HoaDonDieuChinhGiaHangBanPageContent() {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);
  const { isExporting } = useDonHangMuaTrongNuocSearchContext();
  const {
    data: donMuaHangs,
    isLoading,
    addItem: addDonMuaHang,
    updateItem: updateDonMuaHang,
    deleteItem: deleteDonMuaHang,
    refreshData: refreshDonMuaHangs,
    detail,
    fetchDetail,
    totalItems,
    currentPage,
    handlePageChange,
    sumTotal,
    searchData,
    isFiltered
  } = useCRUD<DonMuaHang, DonMuaHangInput>({
    endpoint: QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC,
    pageSize: 20,
    sumField: 't_tt'
  });
  const searchFieldState = useSearchFieldState();
  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    showBulkDelete,
    handleCloseBulkDelete,
    handleRowClick,
    clearSelection,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms(fetchDetail);

  const {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering
  } = useSearchTable({
    searchData,
    searchParams,
    setSearchParams,
    handleViewClick,
    sumTotal,
    isFiltered,
    handlePageChange
  });

  const handleSearch = () => {
    setShowSearchDialog(true);
  };

  const handleSearchClose = () => {
    setShowSearchDialog(false);
  };

  const handleSearchSubmit = async (filters: MuaHangRecord) => {
    setSearchParams(filters);
    await handleInitialSearch(filters);
    handleSearchClose();
  };

  const handleSubmit = async (data: DonMuaHangInput) => {
    try {
      if (formMode === 'add') {
        await addDonMuaHang(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateDonMuaHang(selectedObj.uuid, data);
      }
      handleCloseForm();
      clearSelection();
      await refreshDonMuaHangs();
    } catch (error) {
      return;
    }
  };

  const displayData = hasFilterParams ? searchState.searchResults : donMuaHangs || [];
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedSumTotal = hasFilterParams ? (searchState.totalRow?.t_tt ?? sumTotal) : sumTotal;

  const tables = [
    {
      name: 'Tất cả',
      rows: displayData,
      columns: getDataTableColumns(handleViewClick, resolvedSumTotal, isLoading)
    },
    {
      name: 'Lập chứng từ',
      rows: displayData.filter((row: DonMuaHang) => row.status === '0'),
      columns: getDataTableColumns(handleViewClick, sumTotal, isLoading),
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#EAD1DC]' />
    },
    {
      name: 'Chờ duyệt',
      rows: displayData.filter((row: DonMuaHang) => row.status === '1'),
      columns: getDataTableColumns(handleViewClick, resolvedSumTotal, isLoading),
      icon: <div className='mr-2 size-[7px] rounded-full bg-red-500' />
    },
    {
      name: 'Đã duyệt',
      rows: displayData.filter((row: DonMuaHang) => row.status === '5'),
      columns: getDataTableColumns(handleViewClick, resolvedSumTotal, isLoading),
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#3D85C6]' />
    },
    {
      name: 'Đã hủy',
      rows: displayData.filter((row: DonMuaHang) => row.status === '9'),
      columns: getDataTableColumns(handleViewClick, resolvedSumTotal, isLoading),
      icon: <div className='mr-2 size-[7px] rounded-full bg-gray-500' />
    }
  ];

  return (
    <div className='flex h-screen w-screen flex-col overflow-hidden'>
      {showSearchDialog && (
        <SearchDialog
          open={showSearchDialog}
          onClose={handleSearchClose}
          onSearch={handleSearchSubmit}
          initialSearchValues={searchParams}
          searchField={searchFieldState}
        />
      )}

      {showForm && (
        <FormDialog
          formMode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onSubmit={handleSubmit}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
        />
      )}

      {showDelete && (
        <ConfirmationDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={async () => {
            if (selectedObj?.uuid) {
              await deleteDonMuaHang(selectedObj.uuid);
            }
            clearSelection();
            handleCloseDelete();
          }}
        />
      )}

      {showBulkDelete && (
        <BulkDeleteDialogV4
          title='Đơn hàng mua trong nước'
          onClose={handleCloseBulkDelete}
          columns={getDataTableColumns(handleViewClick) as MuaHangRecord}
          dataRows={displayData}
          handleDelete={deleteDonMuaHang}
        />
      )}

      {!showForm && (
        <>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCopy={handleCopyClick}
            onSearch={handleSearch}
            onRefresh={async () => {
              if (hasFilterParams) {
                await handleRefreshClick();
              } else if (isFiltered) {
                await searchData(searchParams);
              } else {
                await refreshDonMuaHangs();
              }
              if (selectedObj?.uuid) {
                await fetchDetail(selectedObj?.uuid);
              }
            }}
            isEditDisabled={!selectedObj}
          />

          <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
            <ResizablePanelGroup direction='vertical' className='h-full overflow-hidden'>
              <ResizablePanel defaultSize={55} minSize={20}>
                <div className='h-full w-full overflow-hidden'>
                  <ErpDataTableV2
                    tables={tables}
                    selectedRowId={selectedRowIndex || undefined}
                    onRowClick={handleRowClick}
                    getRowId={row => row.uuid}
                    pageSize={20}
                    totalItems={resolvedTotalItems}
                    currentPage={resolvedCurrentPage}
                    onPageChange={onPageChange}
                    onSearchChange={handleSearchChange}
                    loading={isLoading}
                  />
                </div>
              </ResizablePanel>

              <ResizableHandle withHandle className='bg-slate-200/80 hover:bg-slate-300' />

              <ResizablePanel defaultSize={45} minSize={20}>
                <div className='h-full overflow-hidden'>
                  <ErpInputTableV2
                    rows={detail?.chi_tiet_data || []}
                    columns={getInputTableColumns(detail?.chi_tiet_data || [])}
                    getRowId={row => row.uuid}
                    actions={{
                      export: () => console.log('Export clicked'),
                      pin: () => console.log('Pin clicked')
                    }}
                    actionsDisabled={formMode === 'view'}
                    actionOrder={['export', 'pin']}
                    height='100%'
                    loading={isLoading && !selectedObj}
                  />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </>
      )}
    </div>
  );
}

export default function HoaDonDieuChinhGiaHangBanPage() {
  return (
    <DonHangMuaTrongNuocSearchProvider>
      <HoaDonDieuChinhGiaHangBanPageContent />
    </DonHangMuaTrongNuocSearchProvider>
  );
}
